import { SearchBar, Button, Filters, Chip, DropdownSearch, DropdownSelect, Pager } from '@keplerco/core';
import React, { useState } from 'react';
import { SkeletonOverlay } from '../../../components/general/loading-state/skeleton-overlay';
import { DownloadIcon } from '../../../design/icons/download.icon';
import { FiltersOutlet } from '../../../pages/analytics/analytics-filters/filters-outlet';
import { WidgetCard } from '../../../pages/analytics/analytics-filters/widget-card';
import ViewMore from '../../../components/view-more/view-more';
import { SelectedFilterOption } from '../../../pages/analytics/analytics-filters/analytics-filters.models';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { EmptyStateImage } from '../../../components/general/empty-state/empty-state.image';
import { IFeedbackLayoutProps, sentimentOptions } from './feedback.models';
import classNames from 'classnames';
import styles from './feedback.module.css';
import { Sentiment } from '../../../enums/sentiment';

function getSentimentColor(sentiment: Sentiment | undefined): string {
  switch (sentiment) {
    case Sentiment.Positive:
      return 'g';
    case Sentiment.Negative:
      return 'r';
    case Sentiment.Neutral:
      return 'baby-blue';
    default:
      return 'borders';
  }
}

export function FeedbackLayout({ isAnonymous, title, subtitle, data, isLoading, handleDownloadCSV, filtersOptions, handleSearchQuery, handleFiltersChange, handlePageChange, isPerson }: IFeedbackLayoutProps): JSX.Element {
  const showAssessor = !isAnonymous;
  const showSubject = !title.includes('Overall') && !isPerson;

  const [tempFilters, setTempFilters] = useState<SelectedFilterOption[]>([]);
  const [selectedFilters, setSelectedFilters] = React.useState<SelectedFilterOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  function getTempValues(filterType: string): string | undefined {
    return tempFilters.find(f => f.filterType === filterType)?.slug.replace(filterType, '');
  }

  return (
    <SkeletonOverlay isLoading={isLoading} width="100%">
      <WidgetCard title={title} subtitle={subtitle} style={{ width: '100%' }}>
        <div>
          <div style={{ display: 'flex', flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
            <SearchBar
              label="Search"
              loading={isSearching}
              onInput={async event => {
                setIsSearching(true);
                const search = (event.target as HTMLInputElement).value;
                await handleSearchQuery(search);
                setIsSearching(false);
              }}
            />

            <Filters
              onClickApply={async () => {
                handleFiltersChange(tempFilters);
                setSelectedFilters(tempFilters);
              }}
              onClickClear={async () => {
                setTempFilters([]);
                setSelectedFilters([]);
                handleFiltersChange([]);
              }}
            >
              {!!filtersOptions?.skillOptions.length && (
                <DropdownSearch
                  label="Skills"
                  options={filtersOptions.skillOptions ?? []}
                  value={getTempValues('skills')}
                  onChange={event => {
                    const value = event.target.value;
                    const newFilters = tempFilters.filter(f => f.filterType !== 'skills');
                    if (value) {
                      const skill = filtersOptions.skillOptions.find(s => s.value === value);
                      if (skill?.label) {
                        newFilters.push({
                          name: skill.label,
                          filterType: 'skills',
                          slug: `skills${value}`,
                        });
                      }
                    }
                    setTempFilters(newFilters);
                  }}
                  dialogContained
                />
              )}

              {!!filtersOptions?.subjectsOptions?.length && (
                <DropdownSearch
                  label="Subjects"
                  options={filtersOptions.subjectsOptions ?? []}
                  value={getTempValues('subjects')}
                  onChange={event => {
                    const value = event.target.value;
                    const newFilters = tempFilters.filter(f => f.filterType !== 'subjects');
                    if (value) {
                      const subject = filtersOptions.subjectsOptions?.find(s => s.value === value);
                      if (subject?.label) {
                        newFilters.push({
                          name: subject.label,
                          filterType: 'subjects',
                          slug: `subjects${value}`,
                        });
                      }
                    }
                    setTempFilters(newFilters);
                  }}
                  dialogContained
                />
              )}

              {!!filtersOptions?.assessorsOptions?.length && (
                <DropdownSearch
                  label="Assessors"
                  options={filtersOptions.assessorsOptions ?? []}
                  value={getTempValues('assessors')}
                  onChange={event => {
                    const value = event.target.value;
                    const newFilters = tempFilters.filter(f => f.filterType !== 'assessors');
                    if (value) {
                      const assessor = filtersOptions.assessorsOptions?.find(a => a.value === value);
                      if (assessor?.label) {
                        newFilters.push({
                          name: assessor.label,
                          filterType: 'assessors',
                          slug: `assessors${value}`,
                        });
                      }
                    }
                    setTempFilters(newFilters);
                  }}
                  dialogContained
                />
              )}

              <DropdownSelect
                label="Sentiment"
                options={sentimentOptions}
                value={getTempValues('sentiment')}
                onChange={event => {
                  const value = event.target.value;
                  const newFilters = tempFilters.filter(f => f.filterType !== 'sentiment');
                  if (value) {
                    const sentimentOption = sentimentOptions.find(s => s.value === value);
                    if (sentimentOption?.label) {
                      newFilters.push({
                        name: sentimentOption.label,
                        filterType: 'sentiment',
                        slug: `sentiment${value}`,
                      });
                    }
                  }
                  setTempFilters(newFilters);
                }}
              />
            </Filters>

            <div style={{ marginLeft: 'auto' }}>
              <Button
                square
                theme="dark"
                type="button"
                isLoading={isDownloading}
                loaderText="Downloading..."
                onClick={async () => {
                  setIsDownloading(true);
                  await handleDownloadCSV();
                  setIsDownloading(false);
                }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <DownloadIcon />
                  CSV
                </div>
              </Button>
            </div>
          </div>

          <FiltersOutlet
            selectedFilters={selectedFilters}
            removeFilter={async filter => {
              const newTempFilters = tempFilters.filter(f => f.slug !== filter.slug);
              setTempFilters(newTempFilters);
              handleFiltersChange(newTempFilters);
              setSelectedFilters(newTempFilters);
            }}
          />
        </div>

        {!!data?.data.length ? (
          <React.Fragment>
            {data?.data.map((comment, index) => (
              <div key={index} className={classNames('card', styles.container)} style={{ borderLeftColor: `var(--${getSentimentColor(comment.sentiment)})` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {title.includes('Overall') ? (
                    <React.Fragment>
                      <h6>{comment.revieweeName}</h6>
                      {comment.sentiment && <Chip themeColour={getSentimentColor(comment.sentiment)}>{comment.sentiment}</Chip>}
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      <h6>{comment.skillName}</h6>
                      {comment.sentiment && <Chip themeColour={getSentimentColor(comment.sentiment)}>{comment.sentiment}</Chip>}
                    </React.Fragment>
                  )}
                </div>

                <ViewMore>{comment.comment}</ViewMore>

                {(showAssessor || showSubject) && (
                  <div>
                    {showAssessor && (
                      <React.Fragment>
                        <small>Assessor:&nbsp;</small>
                        <small style={{ color: 'var(--text_1)' }}>{comment.reviewerName}</small>
                      </React.Fragment>
                    )}

                    {showSubject && (
                      <React.Fragment>
                        <small>{showAssessor && ' | '}Subject:&nbsp;</small>
                        <small style={{ color: 'var(--text_1)' }}>{comment.revieweeName}</small>
                      </React.Fragment>
                    )}
                  </div>
                )}
              </div>
            ))}

            {data.totalPages > 1 && (
              <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center' }}>
                <Pager
                  activePageNumber={data.currentPage}
                  pageCount={data.totalPages}
                  onPageChange={async (page: number) => {
                    await handlePageChange(page);
                  }}
                />
              </div>
            )}
          </React.Fragment>
        ) : (
          <EmptyState title="Hang tight" subtitle="We are waiting for data" imageElement={<EmptyStateImage />} />
        )}
      </WidgetCard>
    </SkeletonOverlay>
  );
}
